"use client";

import { LoadMoreButton } from "@/components/load-more-button";
import EmptyState from "@/components/portfolio/empty-state";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SmartImage } from "@/components/ui/smart-image";
import { formatMonetaryValue } from "@/utils/utils";
import { useActivityTab } from "../activity/useActivityTab";
import FilterSelect from "../components/filter-select";

interface GuessOption {
    guessNo: string;
    title: string;
}

interface ActivityTabProps {
    eventNo?: string;
    guessList?: GuessOption[];
}

export default function ActivityTab( { eventNo, guessList = [] }: ActivityTabProps ) {
    const {
        activities,
        loading,
        guessOptions,
        amountOptions,
        selectedGuessNo,
        selectedMinAmount,
        handleGuessChange,
        handleAmountChange,
        activityQuery,
    } = useActivityTab( eventNo, guessList );

    const renderEmpty = () => ( loading
        ? (
            <div className="py-24 flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        )
        : <EmptyState text="No activity data" className="h-auto py-24" textClassName="text-sm" /> );

    const renderActivityItem = ( activity: (typeof activities)[number] ) => {
        const tradeAction = activity.tradeType === "split"
            ? "Split"
            : activity.tradeType === "merge"
            ? "Merge"
            : activity.tradeType === "buy"
            ? "Bought"
            : "Sold";

        const betTypeLabel = activity.betType === 1 ? "Yes" : "No";
        const betTypeColorClass = activity.betType === 1 ? "text-yes" : "text-no";
        const amountInCents = formatMonetaryValue( activity.amount );

        const hasAvatar = Boolean( activity.imageUrl );
        // const avatarLetter = activity.betType === 1 ? "Y" : "N";
        // const avatarBgColorVar = activity.betType === 1 ? "var(--color-yes)" : "var(--color-no)";
        const positionLabel = activity.groupTitle
            ? `${activity.groupTitle} · ${activity.guessTitle}`
            : activity.guessTitle;

        return (
            <div className="flex items-center gap-3 rounded-md py-3">
                {/* Avatar */}
                <div className="shrink-0">
                    {hasAvatar
                        ? (
                            <div className="size-11 shrink-0 overflow-hidden rounded-lg bg-surface-muted">
                                <SmartImage
                                    src={activity.imageUrl}
                                    alt={`${activity.userName} avatar`}
                                    width={44}
                                    height={44}
                                    className="size-full object-cover"
                                    imageUrlOptions={{ width: 150, height: 150 }}
                                />
                            </div>
                        )
                        : null}
                </div>

                {/* Content */}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex min-w-0 items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-[14px] font-medium leading-normal text-t1">
                            <span className={betTypeColorClass}>
                                {tradeAction} {activity.guessTypeDesc || betTypeLabel}
                            </span>
                            <span className="text-t1">· {positionLabel}</span>
                        </p>
                        <p className="shrink-0 text-right text-xs font-normal leading-normal text-t3">
                            {activity.time}
                        </p>
                    </div>
                    <p className="text-xs font-normal leading-normal text-t3">
                        {activity.contracts.toLocaleString()} shares ({amountInCents})
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col">
            {/* Filters */}
            {( guessOptions.length > 1 || amountOptions.length > 1 ) && (
                <div className="flex w-full flex-row items-center gap-3 overflow-hidden">
                    {/* Guess Filter */}
                    <FilterSelect
                        value={selectedGuessNo}
                        onValueChange={handleGuessChange}
                        options={guessOptions}
                        placeholder="Select guess"
                        className="min-w-0 flex-1"
                    />

                    {/* Amount Filter */}
                    <FilterSelect
                        value={selectedMinAmount}
                        onValueChange={handleAmountChange}
                        options={amountOptions}
                        placeholder="Min Amount"
                        renderValue={(
                            currentValue,
                            option,
                        ) => ( currentValue === "none" ? "Min Amount" : option?.label ?? "" )}
                        className="min-w-0 flex-1"
                    />
                </div>
            )}

            {/* Activity List */}
            <div className="flex flex-col">
                {loading && activities.length === 0
                    ? (
                        <div className="py-24 flex items-center justify-center">
                            <LoadingSpinner size="lg" />
                        </div>
                    )
                    : activities.length === 0
                    ? ( renderEmpty() )
                    : (
                        <>
                            {activities.map( ( activity, index ) => (
                                <div key={activity.id}>{renderActivityItem( activity )}</div>
                            ) )}

                            {/* Load More Button */}
                            {activityQuery.hasNextPage && activities.length > 0 && (
                                <div className="mt-3">
                                    <LoadMoreButton query={activityQuery} />
                                </div>
                            )}
                        </>
                    )}
            </div>
        </div>
    );
}
